import React, { useEffect, useState } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import "../shared/worksheets/ot-types/ot-patch";

import { MonitoringMessageType } from "../shared/types/monitoring";
import { type PubsubMessageType } from "../shared/types/pubsub";
import { type WebsocketErrorType } from "../shared/types/websocket-auth";

import { PingComponent } from "./PingComponent";

import { useAppDispatch, useAppSelector } from "store/index";
import { selectCurrentSectionId, selectCurrentUserId, selectUserJWT } from "store/selectors";
import { monitoringStateActions } from "store/slices/monitoring";
import { rollbarAndLogError } from "utils/logger";

// The minimum delay should be longer than the longest time we expect congested
// traffic to delay a message. Otherwise, ShareDB will think the connection is
// lost and try to reconnect, which can cause a loop of reconnecting and disconnecting.
const MIN_RECONNECTION_DELAY_MS = 3000;
// The maximum number of times we will try to reconnect before giving up, currently a total
// of MIN_RECONNECTION_DELAY_MS * MAX_RETRY_TIMES = 5 minutes.
const MAX_RETRY_TIMES = 100;

export interface MonitoringWebsocketContextType {
  websocketStatus: string;
  retryConnection: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const MonitoringWebsocketContext = React.createContext<null>(null);

export const MonitoringWebsocketProvider = ({ children }: { children: React.ReactNode }) => {
  // Duplicated from WebsocketProvider.tsx, minus the sharedb connection initialization
  const sectionId = useAppSelector(selectCurrentSectionId);
  const jwt = useAppSelector(selectUserJWT);
  const user_id = useAppSelector(selectCurrentUserId);

  const [webSocket, setWebSocket] = useState<ReconnectingWebSocket | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!jwt || !user_id || !sectionId) return;

    const socketUrl = `${window._env_.MONITORING_WEBSOCKET_URL}?jwt=${jwt}&section_id=${sectionId}`;
    const socket = new ReconnectingWebSocket(`${socketUrl}`, [], {
      // TODO: are these options needed?
      maxRetries: MAX_RETRY_TIMES,
      maxEnqueuedMessages: 0,
      minReconnectionDelay: MIN_RECONNECTION_DELAY_MS,
    });

    setWebSocket(socket);

    const handleMonitoringMessage = (message: MonitoringMessageType) => {
      dispatch(monitoringStateActions.updateMonitoringStats(message));
    };

    socket.addEventListener("message", (evt: MessageEvent) => {
      try {
        if (typeof evt.data !== "string") {
          rollbarAndLogError("Socket got message evt.data of unexpected type", typeof evt.data);
          return;
        }

        const data = JSON.parse(evt.data) as
          | PubsubMessageType
          | { type: "error"; error: WebsocketErrorType };

        const messages = data.type && data.type === "pubsub" ? data.messages : [];

        for (const message of messages) {
          if (message.type && message.type === "monitoring-update") {
            handleMonitoringMessage(message);
          }
        }

        if ("error" in data && data.error) {
          const code = data.error.code;
          // All of these send rollbar messages on the backend, so we don't need to do that here
          if (code === 403) {
            // An operation was rejected due to missing permissions.
            console.info("Monitoring websocket got 403 error", data.error);
          } else if (code === 401) {
            console.info("Monitoring websocket got 401 error", data.error);
          } else {
            console.error("Monitoring websocket got unknown error", data.error);
          }
        }
      } catch (err) {
        console.error("Error reading socket data", err, evt.data);
      }
    });
  }, [dispatch, jwt, user_id, sectionId]);

  return (
    <>
      {webSocket && <PingComponent connectionType="monitoring" socket={webSocket} />}
      <MonitoringWebsocketContext.Provider value={null}>
        {children}
      </MonitoringWebsocketContext.Provider>
    </>
  );
};
